import { useCommunityDetail, useUpdateCommunity } from '@communities/community/hooks';
import { removeSecurityGroup } from '@communities/community/utils/securityGroupsUtils';
import { useUpdateCommunityGroup, useCommunityGroupDetail } from '@communities/community-groups/hooks';
import type { EntityType, SecurityGroups } from '@communities/content-management/domain';
import { isCommunity } from '@communities/content-management/utils';
import { DataList, type TableColumn } from '@core/components';
import { SectionHeader } from '@core/components';
import { IconButton, Info16, IconButtonSize, IconSize, Text, Badge, Button } from '@soteria/design-system-ve';
import { useState, useCallback, useMemo } from 'react';

import { SecurityGroupDialog } from './SecurityGroupDialog';
import { SecurityGroupsMoreInfoDialog } from './SecurityGroupsMoreInfoDialog';

interface SecurityGroupsSectionProps {
  entityId: string;
  entityType: EntityType;
  isEntityAdmin: boolean;
}

interface SecurityGroupItem {
  id: string;
  groupName: string;
  role: 'admin' | 'moderator' | 'user';
}

// eslint-disable-next-line complexity
export function SecurityGroupsSection({ entityId, entityType, isEntityAdmin }: SecurityGroupsSectionProps) {
  const [isMoreInfoDialogOpen, setIsMoreInfoDialogOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSecurityGroup, setSelectedSecurityGroup] = useState<SecurityGroupItem | null>(null);

  // Hooks for communities
  const { community, errors: communityErrors, loading: communityLoading } = useCommunityDetail();
  const { updateCommunity, isUpdating: communityUpdateLoading, errors: communityUpdateErrors, resetErrors: resetCommunityErrors } = useUpdateCommunity();

  // Hooks for community groups
  const {
    updateCommunityGroup,
    loading: communityGroupUpdateLoading,
    errors: communityGroupUpdateErrors,
    resetErrors: resetCommunityGroupErrors,
  } = useUpdateCommunityGroup(!isCommunity(entityType) ? entityId : undefined);
  const { data: communityGroup, loading: communityGroupLoading, errors: communityGroupErrors } = useCommunityGroupDetail();

  // Use appropriate data based on entity type
  const securityGroups: SecurityGroups = useMemo(() => {
    if (isCommunity(entityType)) {
      return community?.securityGroups || { admin: [], moderator: [], user: [] };
    } else {
      return communityGroup?.securityGroups || { admin: [], moderator: [], user: [] };
    }
  }, [entityType, community?.securityGroups, communityGroup?.securityGroups]);

  const loading = isCommunity(entityType) ? communityLoading : communityGroupLoading;
  const isDeleting = isCommunity(entityType) ? communityUpdateLoading : communityGroupUpdateLoading;
  const errors = isCommunity(entityType) ? communityErrors : communityGroupErrors;
  const deleteErrors = isCommunity(entityType) ? communityUpdateErrors : communityGroupUpdateErrors;
  const resetErrors = isCommunity(entityType) ? resetCommunityErrors : resetCommunityGroupErrors;

  // Flatten security groups for DataList
  const securityGroupItems = useMemo(() => {
    if (!securityGroups) return [];

    const items: SecurityGroupItem[] = [];

    // Add admin groups
    securityGroups.admin.forEach((groupName, index) => {
      items.push({
        id: `admin-${index}-${groupName}`,
        groupName,
        role: 'admin',
      });
    });

    // Add moderator groups
    securityGroups.moderator.forEach((groupName, index) => {
      items.push({
        id: `moderator-${index}-${groupName}`,
        groupName,
        role: 'moderator',
      });
    });

    // Add user groups
    securityGroups.user.forEach((groupName, index) => {
      items.push({
        id: `user-${index}-${groupName}`,
        groupName,
        role: 'user',
      });
    });

    return items;
  }, [securityGroups]);

  const columns: TableColumn<SecurityGroupItem>[] = [
    {
      key: 'groupName',
      header: 'Security Group Name',
      render: (item: SecurityGroupItem) => <Text>{item.groupName}</Text>,
      width: '50%',
    },
    {
      key: 'role',
      header: 'Role',
      render: (item: SecurityGroupItem) => {
        const roleText = item.role === 'admin' ? 'Admin' : item.role === 'moderator' ? 'Moderator' : 'User';

        return <Badge text={roleText} />;
      },
      width: '30%',
    },
    {
      key: 'created',
      header: 'Status',
      render: () => <Text>Active</Text>,
      width: '20%',
    },
  ];

  const handleOpenDialog = useCallback((securityGroup?: SecurityGroupItem) => {
    setSelectedSecurityGroup(securityGroup || null);
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setSelectedSecurityGroup(null);
    setIsDialogOpen(false);
  }, []);

  const handleDeleteSecurityGroup = useCallback(
    async (item: SecurityGroupItem): Promise<boolean> => {
      try {
        const updatedSecurityGroups = removeSecurityGroup(securityGroups, item.groupName, item.role);

        if (entityId) {
          // Remove __typename from the security groups object to avoid GraphQL input validation errors
          const cleanSecurityGroups = {
            admin: updatedSecurityGroups.admin || [],
            moderator: updatedSecurityGroups.moderator || [],
            user: updatedSecurityGroups.user || [],
          };

          let success = false;

          if (isCommunity(entityType)) {
            success = await updateCommunity(entityId, {
              securityGroups: cleanSecurityGroups,
            });
          } else {
            success = await updateCommunityGroup(entityId, {
              securityGroups: cleanSecurityGroups,
            });
          }

          return success;
        }

        return false;
      } catch (error) {
        console.error('Failed to delete security group:', error);

        return false;
      }
    },
    [entityId, entityType, securityGroups, updateCommunity, updateCommunityGroup]
  );

  // Create the delete item context
  const deleteItemContext = isEntityAdmin
    ? {
        handler: handleDeleteSecurityGroup,
        isDeleting,
        errors: deleteErrors,
        resetErrors,
      }
    : undefined;

  return (
    <>
      <SectionHeader
        description={`Security groups configured for this ${isCommunity(entityType) ? 'community' : 'community group'}. These groups map external identity provider groups to platform roles.`}
        endComponent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconButton
              aria-label="Learn more about security groups"
              buttonSize={IconButtonSize.Medium}
              icon={Info16}
              iconSize={IconSize.XSmall}
              onClick={() => setIsMoreInfoDialogOpen(true)}
            />
            {isEntityAdmin && <Button onClick={() => handleOpenDialog()}>ADD SECURITY GROUP</Button>}
          </div>
        }
        title={`Security Groups (${securityGroupItems.length})`}
      />

      {/* Security Groups List */}
      <DataList
        columns={columns}
        data={securityGroupItems}
        dataTypeName="security group"
        deleteItemContext={deleteItemContext}
        error={errors}
        isLoading={loading}
        onEdit={isEntityAdmin ? handleOpenDialog : undefined}
      />

      {/* Create/Edit Security Group Dialog */}
      <SecurityGroupDialog
        editingGroup={selectedSecurityGroup}
        entityId={entityId}
        entityType={entityType}
        existingGroups={securityGroupItems}
        isOpen={isDialogOpen}
        securityGroups={securityGroups}
        onClose={handleCloseDialog}
      />

      {/* More Info Dialog */}
      <SecurityGroupsMoreInfoDialog isOpen={isMoreInfoDialogOpen} onClose={() => setIsMoreInfoDialogOpen(false)} />
    </>
  );
}
